import React, { useState } from 'react';
import { useSessionContext } from '../context/SessionContext';
import { useThemeContext } from '../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { FiTrendingUp, FiTarget, FiClock, FiAward, FiCalendar } from 'react-icons/fi';

const Analytics = () => {
  const { sessions } = useSessionContext();
  const { theme } = useThemeContext();
  const [timeRange, setTimeRange] = useState('3months');

  // Calculate real analytics from actual sessions
  const calculateAnalytics = () => {
    if (sessions.length === 0) {
      return {
        monthlySessionData: [],
        submissionTypeData: [],
        positionTimeData: [],
        progressData: [],
        stats: {
          totalSessions: 0,
          totalMatTime: 0,
          averageSessionLength: 0,
          winRate: 0,
          submissionsPerSession: 0,
          favoritePosition: 'N/A'
        }
      };
    }

    // Calculate stats
    const totalSessions = sessions.length;
    const totalMatTime = sessions.reduce((sum, s) => sum + (s.duration || 0), 0);
    const averageSessionLength = Math.round(totalMatTime / totalSessions);
    const totalWins = sessions.reduce((sum, s) => sum + (s.wins || 0), 0);
    const totalLosses = sessions.reduce((sum, s) => sum + (s.losses || 0), 0);
    const winRate =
      totalWins + totalLosses > 0 ? Math.round((totalWins / (totalWins + totalLosses)) * 100) : 0;

    // Calculate submissions per session
    const totalSubmissions = sessions.reduce(
      (sum, s) =>
        sum +
        (s.techniques?.filter((t) => 
          t.outcome === 'Succeeded' && 
          ['choke', 'lock', 'bar'].some(term => t.name.toLowerCase().includes(term))
        ).length || 0),
      0
    );
    const submissionsPerSession = totalSessions > 0 ? (totalSubmissions / totalSessions).toFixed(1) : 0;

    // Calculate monthly session data
    const monthlyMap = {};
    sessions.forEach((session) => {
      const date = new Date(session.date);
      const monthKey = date.toLocaleDateString('en-US', { month: 'short' });
      if (!monthlyMap[monthKey]) {
        monthlyMap[monthKey] = { month: monthKey, sessions: 0, matTime: 0 };
      }
      monthlyMap[monthKey].sessions++;
      monthlyMap[monthKey].matTime += session.duration || 0;
    });
    const monthlySessionData = Object.values(monthlyMap).slice(-4);

    // Calculate submission types
    const submissionMap = {};
    sessions.forEach((session) => {
      session.techniques?.forEach((tech) => {
        if (tech.outcome === 'Succeeded') {
          const category = tech.name.toLowerCase().includes('choke')
            ? 'Chokes'
            : tech.name.toLowerCase().includes('lock')
            ? 'Joint Locks'
            : tech.name.toLowerCase().includes('leg')
            ? 'Leg Locks'
            : 'Other';
          submissionMap[category] = (submissionMap[category] || 0) + 1;
        }
      });
    });

    const colors = {
      Chokes: '#1e40af',
      'Joint Locks': '#dc2626',
      'Leg Locks': '#059669',
      Other: '#d97706'
    };

    const submissionTypeData = Object.entries(submissionMap).map(([name, value]) => ({
      name,
      value,
      color: colors[name]
    }));

    // Calculate position time (placeholder - would need position tracking)
    const positionTimeData = [];

    // Calculate progress data (last 6 weeks)
    const progressData = [];

    // Find favorite position (placeholder)
    const favoritePosition = 'N/A';

    return {
      monthlySessionData,
      submissionTypeData,
      positionTimeData,
      progressData,
      stats: {
        totalSessions,
        totalMatTime,
        averageSessionLength,
        winRate,
        submissionsPerSession,
        favoritePosition
      }
    };
  };

  const analytics = calculateAnalytics();
  const { monthlySessionData, submissionTypeData, stats } = analytics;

  const formatMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  // Recharts Dark Mode Properties
  const isDark = theme === 'dark';
  const axisColor = isDark ? '#94a3b8' : '#64748b';
  const gridColor = isDark ? '#334155' : '#e2e8f0';
  const tooltipStyle = {
    backgroundColor: isDark ? '#1e293b' : '#ffffff',
    borderColor: isDark ? '#334155' : '#e2e8f0',
    color: isDark ? '#f8fafc' : '#0f172a'
  };

  // Show empty state if no sessions
  if (sessions.length === 0) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">Progress Analytics</h1>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">
            Track your development and identify areas for improvement.
          </p>
        </div>

        <div className="text-center py-12">
          <FiTrendingUp className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No Analytics Data Yet</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Start logging training sessions to see your progress analytics and insights.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">Progress Analytics</h1>
            <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">
              Track your development and identify areas for improvement.
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
            >
              <option value="1month">Last Month</option>
              <option value="3months">Last 3 Months</option>
              <option value="6months">Last 6 Months</option>
              <option value="1year">Last Year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Sessions</p>
                <FiCalendar className="w-5 h-5 sm:w-8 sm:h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">{stats.totalSessions}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Mat Time</p>
                <FiClock className="w-5 h-5 sm:w-8 sm:h-8 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
                  {formatMinutes(stats.totalMatTime)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Win Rate</p>
                <FiAward className="w-5 h-5 sm:w-8 sm:h-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">{stats.winRate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Avg Session</p>
                <FiTarget className="w-5 h-5 sm:w-8 sm:h-8 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
                  {formatMinutes(stats.averageSessionLength)}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{stats.submissionsPerSession} subs/session</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
        {/* Training Volume */}
        {monthlySessionData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Training Volume</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthlySessionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis dataKey="month" tick={{ fill: axisColor, fontSize: 12 }} />
                  <YAxis tick={{ fill: axisColor, fontSize: 12 }} />
                  <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: isDark ? '#cbd5e1' : '#334155' }} />
                  <Legend wrapperStyle={{ fontSize: '12px', color: isDark ? '#cbd5e1' : '#334155' }} />
                  <Bar dataKey="sessions" fill="#1e40af" name="Sessions" />
                  <Bar dataKey="matTime" fill="#059669" name="Mat Time (min)" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Submission Types */}
        {submissionTypeData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Submission Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={submissionTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {submissionTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: isDark ? '#cbd5e1' : '#334155' }} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Analytics;